import cx from 'classnames';

import CreateAction from '~/components/Actions/CreateAction';
import PostImage from '~/components/PostCardv2/PostImage';
import PostLink from '~/components/PostCardv2/PostLink';
import Premium from '~/components/PostCardv2/Premium';
import Titles from '~/components/PostCardv2/Titles';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { Post } from '~/interfaces/post';
import type { HomePageLoaderData } from '~/routes';
import normalizeFontType from '~/utils/normalizeFontType';

const getHeaderPosts = (posts: Post[], featuredPosts: Post[]) => {
  let headliner: any;
  const leftPanelPosts: Post[] = [];
  const rightPanelPosts: Post[] = [];

  // Fill with featured posts first
  featuredPosts.forEach((post, index) => {
    if (index === 0) {
      headliner = post;
    } else if (leftPanelPosts.length < 2) {
      leftPanelPosts.push(post);
    } else {
      rightPanelPosts.push(post);
    }
  });

  // For aesthetics fill out the news section with latest posts
  let rightPanelPostsCount = rightPanelPosts.length;
  let leftPanelPostsCount = leftPanelPosts.length;
  posts.forEach((post) => {
    if (!headliner) {
      headliner = post;
      return;
    }

    if (leftPanelPostsCount < 2) {
      leftPanelPosts.push(post);
      leftPanelPostsCount++;
      return;
    }

    if (rightPanelPostsCount >= 4) {
      return;
    }

    rightPanelPosts.push(post);
    rightPanelPostsCount++;
  });

  return {
    headliner: headliner,
    leftPanelPosts,
    rightPanelPosts,
  };
};

const News = () => {
  const { homePage, publication, paginatedPosts, featuredPosts } = useTypedLoaderData<HomePageLoaderData>();

  const posts = paginatedPosts.posts;
  const { headliner, leftPanelPosts, rightPanelPosts } = getHeaderPosts(posts, featuredPosts);

  const width = homePage?.general_section?.header?.width;

  return (
    <div className="w-full px-4 py-12 pt-8">
      <div
        style={width === 'full' ? { maxWidth: '1600px' } : {}}
        className={cx(
          'mx-auto grid grid-cols-12 gap-0 transition-all md:gap-6',
          width === 'full' ? 'w-full' : 'max-w-6xl',
        )}
      >
        {/* Left Panel */}
        <div className="col-span-12 w-full md:col-span-3 md:block">
          <div className="space-y-2">
            {leftPanelPosts.map((post, _index) => {
              const isPremium = post.audience === 'premium';

              return (
                <PostLink key={post.id} slug={post?.slug} className={cx('group flex w-full flex-col transition-all')}>
                  <PostImage alt={post.web_title} src={post.image_url} />

                  <div className={cx('flex flex-col justify-between space-y-6 py-4')}>
                    <div className="space-y-1">
                      {isPremium && (
                        <Premium
                          fontFamily={homePage?.general_section?.theme?.font}
                          color={homePage?.general_section?.theme?.accent_color}
                        />
                      )}
                      <Titles
                        title={post.web_title}
                        subtitle={post?.web_subtitle}
                        fontFamily={homePage?.general_section?.theme?.font}
                        titleSize="xl"
                        subtitleSize="md"
                        titleColor={homePage?.general_section?.theme?.text_color}
                        subtitleColor={homePage?.general_section?.theme?.text_color}
                        textClassName="group-hover:underline"
                      />
                    </div>
                  </div>
                </PostLink>
              );
            })}
          </div>
        </div>

        {/* Center Panel */}
        <div className="col-span-12 w-full space-y-4 md:col-span-9 lg:col-span-6">
          {headliner ? (
            <PostLink slug={headliner?.slug} className={cx('group flex w-full flex-col transition-all')}>
              <PostImage alt={headliner?.web_title} src={headliner?.image_url} />

              <div className={cx('flex flex-col justify-between space-y-6 py-4')}>
                <div className="space-y-3">
                  {headliner?.audience === 'premium' && (
                    <Premium
                      fontFamily={homePage?.general_section?.theme?.font}
                      color={homePage?.general_section?.theme?.accent_color}
                    />
                  )}
                  <Titles
                    title={headliner?.web_title}
                    subtitle={headliner?.web_subtitle}
                    fontFamily={homePage?.general_section?.theme?.font}
                    titleSize="2xl"
                    subtitleSize="lg"
                    titleColor={homePage?.general_section?.theme?.text_color}
                    subtitleColor={homePage?.general_section?.theme?.text_color}
                    textClassName="group-hover:underline"
                  />
                </div>
              </div>
            </PostLink>
          ) : null}
          <CreateAction
            publication={publication}
            fontFamily={normalizeFontType(homePage.main_section.signup_widget.font)}
            placeholderText={homePage.main_section.signup_widget.placeholder_text}
            cta={homePage.main_section.signup_widget.cta_text}
            textColor={homePage.main_section.signup_widget.text_color}
            backgroundColor={homePage.main_section.signup_widget.bg_color}
          />
        </div>

        {/* Right Panel */}
        <div className="col-span-12 w-full lg:col-span-3 lg:block">
          <div className="flex flex-col space-y-2">
            {rightPanelPosts.map((post, _index) => {
              const isPremium = post.audience === 'premium';

              return (
                <div
                  key={post.id}
                  className="border-b"
                  style={{
                    borderColor: homePage?.general_section?.theme?.border_color,
                  }}
                >
                  <PostLink
                    slug={post?.slug}
                    className={cx('group relative flex w-full flex-col space-y-2 py-4 pb-4 transition-all')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="">
                        {isPremium && (
                          <Premium
                            fontFamily={homePage?.general_section?.theme?.font}
                            color={homePage?.general_section?.theme?.accent_color}
                          />
                        )}
                        <Titles
                          title={post.web_title}
                          subtitle={post?.web_subtitle}
                          textClassName="group-hover:underline"
                          fontFamily={homePage?.general_section?.theme?.font}
                          titleSize="md"
                          subtitleSize="xs"
                          titleColor={homePage?.general_section?.theme?.text_color}
                          subtitleColor={homePage?.general_section?.theme?.text_color}
                        />
                      </div>
                    </div>
                  </PostLink>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default News;
